import React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Box, Checkbox, Chip, Typography } from "@material-ui/core";
import clsx from "clsx";
import StatusBadge from "./StatusBadge";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: "13%",
      borderRight: "1px solid #ccc",
      overflowY: "auto",
      height: "100%",
      backgroundColor: "#fff",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px",
      borderBottom: "1px solid #ccc",
      position: "sticky",
      top: 0,
      backgroundColor: "#fff",
      zIndex: 1,
    },
    ul: { listStyle: "none", padding: 0, margin: 0 },
    li: {
      padding: "10px",
      borderBottom: "1px solid #eee",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      "&:hover": { backgroundColor: "#f9f9f9" },
      transition: "background-color 0.2s ease-in-out",
    },
    liSelected: { backgroundColor: "#eaeaea" },
    nameRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      marginLeft: theme.spacing(1),
      minWidth: 0,
    },
    name: {
      fontSize: "0.9rem",
      fontWeight: 500,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    badge: { marginLeft: theme.spacing(1) },
    dncChip: {
      backgroundColor: "#f44336",
      color: "#fff",
      height: 20,
      fontWeight: 600,
    },
  })
);

export type CadenceRecordListProps = {
  records: any[];
  selectedPerson: any;
  selectedProspectIds: Set<string>;
  onSelectPerson: (person: any) => void;
  onToggleCheck: (id: string, checked: boolean) => void;
  activeRecordId?: string;
  title?: string;
  activeLeadId?: string;
  activeStatus?:
    | 'registering'
    | 'registered'
    | 'calling'
    | 'connected'
    | 'ended'
    | 'failed';
  isCompleted?: (id: string) => boolean;
  isSkipped?: (id: string) => boolean;
};

const CadenceRecordList: React.FC<CadenceRecordListProps> = ({
  records,
  selectedPerson,
  selectedProspectIds,
  onSelectPerson,
  onToggleCheck,
  activeRecordId,
  activeStatus,
  title = "Prospects",
  isCompleted,
  isSkipped,
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <ul className={classes.ul}>
        {records.map((person, index) => {
          const personId =
            person?.cadenceContactInfo?.contactId ||
            person?.cadenceContactInfo?.leadId;
          const cadenceId = person?.cadenceContactInfo?.cadencesContactDataId;
          const isSelected =
            selectedPerson?.cadenceContactInfo?.leadId === personId ||
            selectedPerson?.cadenceContactInfo?.contactId === personId;
          const isChecked = selectedProspectIds.has(cadenceId);
          const isDNC = Boolean(person?.cadenceContactInfo?.doNotCall);
          const isCompletedRecord = isCompleted?.(cadenceId);
          const isSkippedRecord = isSkipped?.(cadenceId);

          return (
            <li
              key={`${personId}-${cadenceId ?? index}`}
              className={clsx(classes.li, {
                [classes.liSelected]: isSelected,
              })}
              onClick={() => onSelectPerson(person)}
              style={{
                backgroundColor: isSkippedRecord
                  ? "#fff3e0"
                  : "transparent",
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={(e) => onToggleCheck(cadenceId, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                color="primary"
                size="small"
              />

              <div className={classes.nameRow}>
                <Typography className={classes.name} noWrap>
                  {`${person?.firstName ?? ""} ${person?.lastName ?? ""}`}
                </Typography>

                {isDNC && (
                  <Chip
                    className={classes.dncChip}
                    label="DO NOT CALL"
                    size="small"
                  />
                )}

                {activeRecordId === personId &&
                  ["calling", "connected", "ended"].includes(
                    activeStatus ?? ""
                  ) && (
                    <StatusBadge
                      status={activeStatus}
                      className={classes.badge}
                    />
                  )}
              </div>
            </li>
          );
        })}
      </ul>
    </Box>
  );
};

export default CadenceRecordList;
